/**
 * FanOutChips — the captured-once-satisfies-all visualization at the
 * field level. Drops below any form input and shows a tight row of
 * consumer chips (NEM · PRT · NRR · CST · STR · ...) representing
 * every downstream form / regulator / billing system this single
 * keystroke just satisfied.
 *
 * Tap a chip → opens the Data Pipeline diagram pre-focused on that
 * capture so the user / supervisor can trace where the data goes.
 *
 * Data-driven from lib/pipeline-graph.ts. Pass the canonical
 * capture id (e.g. "situation.chief_complaint") and the component
 * looks up the feeds + NEMSIS element ids automatically.
 */

import { useNavigate } from 'react-router-dom';
import { CAPTURES, CONSUMERS, type Consumer } from '@/lib/pipeline-graph';
import { cn } from '@/lib/cn';

const CONSUMER_CATEGORY_BG: Record<Consumer['category'], string> = {
  regulatory: 'bg-red-100 text-red-800 border-red-300',
  clinical:   'bg-emerald-100 text-emerald-800 border-emerald-300',
  department: 'bg-blue-100 text-blue-800 border-blue-300',
  financial:  'bg-amber-100 text-amber-800 border-amber-300',
};

interface Props {
  /** Canonical capture id from lib/pipeline-graph.ts (e.g. "patient.dob"). */
  capture: string;
  /** Optional class for the wrapping <div>. */
  className?: string;
  /**
   * If false, hide the leading "Feeds:" label so the chips sit
   * perfectly tight under tiny inputs. Default true.
   */
  showLabel?: boolean;
}

export function FanOutChips(_props: Props) {
  // Visually silent — the captured-once fan-out story is told on the
  // build-logic preview page, not at every input below every form
  // row. EMTs in the field don't need a NEM / RI / TOC / NRR / AUD /
  // CST chip strip beneath each control; it was decoration eating
  // vertical space. Component kept as a no-op so call sites don't
  // need to change. Restore the legacy render below if a future
  // admin toggle wants pipeline annotations on.
  return null;
}

// Legacy render preserved for reference / future opt-in mode.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _FanOutChipsLegacy({ capture, className, showLabel = true }: Props) {
  const navigate = useNavigate();
  const cap = CAPTURES.find((c) => c.id === capture);
  if (!cap) return null;
  if (cap.feeds.length === 0) return null;

  const ordered = [...cap.feeds]
    .map((id) => CONSUMERS[id])
    .sort((a, b) => {
      const order: Consumer['category'][] = ['regulatory', 'clinical', 'department', 'financial'];
      return order.indexOf(a.category) - order.indexOf(b.category);
    });

  return (
    <div
      className={cn('flex flex-wrap items-center gap-1 mt-1', className)}
      title={`This input populates ${cap.feeds.length} downstream consumers — captured once, satisfies all.`}
    >
      {showLabel && (
        <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mr-0.5">
          feeds {cap.feeds.length} →
        </span>
      )}
      {ordered.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/data-pipeline?capture=${encodeURIComponent(cap.id)}`);
          }}
          title={`${c.name} — ${c.rationale}`}
          className={cn(
            'inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border hover:scale-110 transition-transform',
            CONSUMER_CATEGORY_BG[c.category],
          )}
        >
          {c.short}
        </button>
      ))}
      {cap.nemsis.length > 0 && (
        <span className="text-[9px] font-mono text-slate-500 ml-1" title={`NEMSIS elements: ${cap.nemsis.join(', ')}`}>
          {cap.nemsis.length === 1 ? cap.nemsis[0] : `${cap.nemsis[0]} +${cap.nemsis.length - 1}`}
        </span>
      )}
    </div>
  );
}
